import { useState, useEffect, useCallback } from 'react';
import { User, UserCredential } from 'firebase/auth';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { realDataService } from '@/shared/services/realDataService';
import { sessionService } from '@/features/auth/services/sessionService';
import type { UserProfile } from '@/shared/types';

interface UseFirebaseAuthReturn {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  signUp: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isAuthenticated: boolean;
  clearError: () => void;
}

export const useFirebaseAuth = (): UseFirebaseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clear error helper
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Handle auth errors
  const handleError = useCallback((err: unknown) => {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message || 'An authentication error occurred';
    setError(message);
    console.error('Auth error:', err);
  }, []);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string): Promise<UserProfile> => {
    try {
      setLoading(true);
      clearError();

      if (realDataService.isUsingRealServices) {
        const profile = await realDataService.login(email, password);
        if (!profile) {
          throw new Error('Invalid email or password');
        }
        return profile;
      } else {
        const result = await firebaseAuthService.signIn(email, password);
        return result;
      }
    } catch (err: unknown) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  // Sign up with email and password
  const signUp = useCallback(async (
    email: string,
    password: string,
    displayName: string
  ): Promise<UserProfile> => {
    try {
      setLoading(true);
      clearError();
      const result = await firebaseAuthService.signUp(email, password, displayName);
      return result;
    } catch (err: unknown) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  // Sign in with Google
  const signInWithGoogle = useCallback(async (): Promise<UserProfile> => {
    try {
      setLoading(true);
      clearError();
      const result = await firebaseAuthService.signInWithGoogle();
      return result;
    } catch (err: unknown) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await firebaseAuthService.signOut();
      await sessionService.endSession();
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  // Reset password
  const resetPassword = useCallback(async (email: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await firebaseAuthService.resetPassword(email);
    } catch (err: unknown) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = firebaseAuthService.onAuthStateChanged(async (authUser) => {
      setUser(authUser);

      // Create basic profile from Firebase user
      if (authUser) {
        const profile = firebaseAuthService.firebaseUserToProfile(authUser);
        setUserProfile(profile);
        // Start session management
        await sessionService.startSession(profile);
      } else {
        setUserProfile(null);
        await sessionService.endSession();
      }

      setLoading(false);
    });

    // Cleanup
    return unsubscribe;
  }, []);

  // Wrapper functions to maintain UserCredential interface
  const signInWrapper = useCallback(async (email: string, password: string): Promise<UserCredential> => {
    const profile = await signIn(email, password);
    return {
      user: firebaseAuthService.getCurrentUser()!,
      credential: null,
      operationType: 'signIn',
      providerId: 'password'
    } as unknown as UserCredential;
  }, [signIn]);

  const signUpWrapper = useCallback(async (email: string, password: string, displayName: string): Promise<UserCredential> => {
    const profile = await signUp(email, password, displayName);
    return {
      user: firebaseAuthService.getCurrentUser()!,
      credential: null,
      operationType: 'signIn',
      providerId: 'password'
    } as unknown as UserCredential;
  }, [signUp]);

  const signInWithGoogleWrapper = useCallback(async (): Promise<UserCredential> => {
    const profile = await signInWithGoogle();
    return {
      user: firebaseAuthService.getCurrentUser()!,
      credential: null,
      operationType: 'signIn',
      providerId: 'google.com'
    } as unknown as UserCredential;
  }, [signInWithGoogle]);

  return {
    user,
    userProfile,
    loading,
    error,
    signIn: signInWrapper,
    signUp: signUpWrapper,
    signInWithGoogle: signInWithGoogleWrapper,
    signOut,
    resetPassword,
    isAuthenticated: firebaseAuthService.isAuthenticated(),
    clearError
  };
};